import { LazyQueryExecFunction, useQuery } from '@apollo/client'
import { isEmpty, uniqBy } from 'lodash'

import { MAX_INT_FOR_PAGINATION_SIZE } from 'config/constants'
import { GET_SEGMENTS_SUGGESTIONS } from 'features/Segments/queries-legacy'
import { GTSegment, GTSegmentSuggestion, GTSegmentSuggestionsResponse } from 'gql/types.generated'

const SEGMENTS_REFETCH_TIMEOUT_SECONDS = 5

export interface ISegmentsHooksRoute {
  id: string
  stops: {
    id: string
    name: string
  }[]
}

export const isSegmentCreationCompleted = (route: ISegmentsHooksRoute, segments: GTSegment[] = []): boolean => {
  const newSegmentsQty = (route.stops.length * (route.stops.length - 1)) / 2
  return newSegmentsQty === segments.length
}

export const getAllSegmentsByRoute = async (
  route: ISegmentsHooksRoute,
  getSegmentsQuery: LazyQueryExecFunction<{ segments: GTSegment[] }, { routeId: string }>
): Promise<GTSegment[]> => {
  const result = await getSegmentsQuery({ variables: { routeId: route.id } })
  if (result.data?.segments && !isSegmentCreationCompleted(route, result.data?.segments || [])) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(getAllSegmentsByRoute(route, getSegmentsQuery))
      }, SEGMENTS_REFETCH_TIMEOUT_SECONDS * 1000)
    })
  }

  return result.data?.segments || []
}

interface IUseAllSegmentsByCitiesOptions {
  companyId: string
  cityOrigins: { id: string }[]
  cityDestinations: { id: string }[]
  isBothWays?: boolean
  onlyNoFareFamily?: boolean
}

interface IUseAllSegmentsByCitiesResponse {
  segmentSuggestions: GTSegmentSuggestion[]
  loading: boolean
}

export const useAllSegmentsByCities = ({
  companyId,
  cityOrigins = [],
  cityDestinations = [],
  isBothWays = false,
  onlyNoFareFamily = false
}: IUseAllSegmentsByCitiesOptions): IUseAllSegmentsByCitiesResponse => {
  const { data: oneWayData, loading: oneWayDataLoading } = useQuery<{
    segmentSuggestions: GTSegmentSuggestionsResponse
  }>(GET_SEGMENTS_SUGGESTIONS, {
    variables: {
      input: {
        companyID: companyId,
        originCityIDs: cityOrigins.map(({ id }) => id),
        destinationCityIDs: cityDestinations.map(({ id }) => id),
        onlyNoFareFamily
      },
      pagination: {
        page: 1,
        size: MAX_INT_FOR_PAGINATION_SIZE
      }
    },
    skip: isEmpty(cityOrigins) || isEmpty(cityDestinations) || isEmpty(companyId)
  })
  const { data: returnData, loading: returnDataLoading } = useQuery<{
    segmentSuggestions: GTSegmentSuggestionsResponse
  }>(GET_SEGMENTS_SUGGESTIONS, {
    variables: {
      input: {
        companyID: companyId,
        originCityIDs: cityDestinations.map(({ id }) => id),
        destinationCityIDs: cityOrigins.map(({ id }) => id),
        onlyNoFareFamily
      },
      pagination: {
        page: 1,
        size: MAX_INT_FOR_PAGINATION_SIZE
      }
    },
    skip: isEmpty(cityOrigins) || isEmpty(cityDestinations) || isEmpty(companyId) || !isBothWays
  })

  const oneWaySegments = oneWayData?.segmentSuggestions?.items || []
  const returnSegments = returnData?.segmentSuggestions?.items || []
  return {
    segmentSuggestions: uniqBy([...oneWaySegments, ...returnSegments], 'id'),
    loading: oneWayDataLoading || returnDataLoading
  }
}
